import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend, Cell,
} from 'recharts';
import { AlertTriangle, Clock, CheckCircle2, ListChecks, TrendingUp, ChevronRight, Download, FileSpreadsheet } from 'lucide-react';

const NAVY = '#0E2A47';
const GREEN = '#5FA718';
const ORANGE = '#C8641E';
const BLUE = '#2F6DB5';
const PRIORITY_COLORS = { P1: '#DC2626', P2: '#EA580C', P3: '#D97706', P4: '#2F6DB5', P5: '#64748B', '—': '#94A3B8' };

const iso = (d) => d.toISOString().slice(0, 10);
const presetRange = (key) => {
  const end = new Date(); const start = new Date();
  if (key === 'last30') start.setDate(end.getDate() - 30);
  else if (key === 'week') start.setDate(end.getDate() - end.getDay());
  else if (key === 'month') { start.setDate(1); }
  else if (key === 'quarter') { const q = Math.floor(end.getMonth() / 3); start.setMonth(q * 3, 1); }
  return { start: iso(start), end: iso(end) };
};

const Kpi = ({ label, value, tone = 'text-[#0E2A47]', icon: Icon, sub, onClick, testid }) => (
  <button onClick={onClick} disabled={!onClick} data-testid={testid}
    className={`rounded-xl border border-gray-200 bg-white p-4 text-left ${onClick ? 'hover:shadow-md hover:border-[#0E2A47]/20 transition-all cursor-pointer' : ''}`}>
    <div className="flex items-center justify-between">
      <div className={`text-3xl font-extrabold ${value ? tone : 'text-slate-300'}`}>{value ?? 0}{sub ? <span className="text-base font-semibold text-slate-400"> {sub}</span> : ''}</div>
      {Icon && <Icon size={20} className="text-slate-300" />}
    </div>
    <div className="mt-1 text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
  </button>
);

const ChartCard = ({ title, children, testid }) => (
  <Card data-testid={testid}>
    <CardContent className="p-4">
      <h3 className="text-sm font-bold text-[#0E2A47] mb-3">{title}</h3>
      <div style={{ width: '100%', height: 240 }}>{children}</div>
    </CardContent>
  </Card>
);

const ReportsView = ({ onDrill }) => {
  const { token } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [preset, setPreset] = useState('last30');
  const [range, setRange] = useState(presetRange('last30'));
  const [customerId, setCustomerId] = useState('');
  const [tradeType, setTradeType] = useState('');
  const [priority, setPriority] = useState('');
  const [customers, setCustomers] = useState([]);
  const [data, setData] = useState(null);
  const [srReport, setSrReport] = useState(null);
  const [srBusy, setSrBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/customers`, authHeaders).then((r) => setCustomers(r.data || [])).catch(() => {});
  }, []);

  const srParams = useCallback(() => {
    const params = { start: range.start, end: range.end };
    if (customerId) params.customer_id = customerId;
    if (priority) params.priority_level = priority;
    return params;
  }, [range, customerId, priority]);

  const loadSr = useCallback(() => {
    setSrBusy(true);
    axios.get(`${API}/reports/service-requests`, { ...authHeaders, params: srParams() })
      .then((r) => setSrReport(r.data))
      .catch(() => setSrReport(null))
      .finally(() => setSrBusy(false));
  }, [srParams]);
  useEffect(() => { loadSr(); }, [loadSr]);

  const exportSrCsv = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/reports/service-requests.csv`, { ...authHeaders, params: srParams(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ROUT_Service_Requests_${range.start || 'all'}_${range.end || 'all'}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { /* silent */ }
    finally { setExporting(false); }
  };

  const load = useCallback(() => {
    const params = { start: range.start, end: range.end };
    if (customerId) params.customer_id = customerId;
    if (tradeType) params.trade_type = tradeType;
    if (priority) params.priority_level = priority;
    axios.get(`${API}/reports/overview`, { ...authHeaders, params }).then((r) => setData(r.data)).catch(() => {});
  }, [range, customerId, tradeType, priority]);
  useEffect(() => { load(); }, [load]);

  const applyPreset = (key) => { setPreset(key); setRange(presetRange(key)); };
  const setStart = (v) => { setPreset('custom'); setRange((p) => ({ ...p, start: v })); };
  const setEnd = (v) => { setPreset('custom'); setRange((p) => ({ ...p, end: v })); };

  const drill = (extra) => {
    const cname = customerId ? (customers.find((c) => c.id === customerId)?.name) : undefined;
    onDrill?.({
      start: range.start, end: range.end,
      customer_id: customerId || undefined, customer_name: cname,
      trade_type: tradeType || undefined, priority: priority || undefined,
      ...extra,
    });
  };

  const statusData = data ? Object.entries(data.by_status).map(([status, count]) => ({ status, count })) : [];
  const priorityData = data ? Object.entries(data.by_priority).map(([p, count]) => ({ priority: p, count })) : [];
  const tradeData = data ? data.by_trade : [];
  const ct = data?.completion_times || {};
  const ov = data?.overdue || {};

  const PRESETS = [['last30', 'Last 30 Days'], ['week', 'This Week'], ['month', 'This Month'], ['quarter', 'This Quarter']];
  const TRADES = ['Janitorial', 'Event Support', 'Landscape', 'Security', 'Sweeping', 'General Maintenance', 'Special Projects', 'HVAC', 'Plumbing', 'Electrical'];

  return (
    <div data-testid="reports-view">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">Operations</div>
        <h1 className="text-3xl font-extrabold text-[#0E2A47]">Reports &amp; Analytics</h1>
        <p className="text-sm text-slate-500 mt-1">Operational insight across work orders, providers and trends. Click any metric to drill into the underlying work orders.</p>
      </div>

      {/* Filter bar */}
      <Card className="mb-6">
        <CardContent className="p-4 flex flex-wrap items-end gap-3" data-testid="reports-filters">
          <div className="flex flex-wrap gap-1">
            {PRESETS.map(([k, lbl]) => (
              <button key={k} onClick={() => applyPreset(k)} data-testid={`preset-${k}`}
                className={`rounded-md px-2.5 py-1.5 text-xs font-medium border ${preset === k ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-slate-600 border-gray-200'}`}>{lbl}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input type="date" value={range.start} onChange={(e) => setStart(e.target.value)} data-testid="reports-start"
              className="rounded-md border border-gray-200 px-2 py-1.5 text-sm" />
            <span className="text-slate-400 text-sm">→</span>
            <input type="date" value={range.end} onChange={(e) => setEnd(e.target.value)} data-testid="reports-end"
              className="rounded-md border border-gray-200 px-2 py-1.5 text-sm" />
          </div>
          <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="reports-customer"
            className="rounded-md border border-gray-200 px-2 py-1.5 text-sm">
            <option value="">All customers</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={tradeType} onChange={(e) => setTradeType(e.target.value)} data-testid="reports-trade"
            className="rounded-md border border-gray-200 px-2 py-1.5 text-sm">
            <option value="">All trades</option>
            {TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="reports-priority"
            className="rounded-md border border-gray-200 px-2 py-1.5 text-sm">
            <option value="">All priorities</option>
            {['P1', 'P2', 'P3', 'P4', 'P5'].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </CardContent>
      </Card>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6" data-testid="reports-kpis">
        <Kpi testid="kpi-total" label="Total (range)" value={data?.totals?.total} icon={ListChecks} onClick={() => drill({})} />
        <Kpi testid="kpi-active" label="Active" value={data?.totals?.active} tone="text-[#2F6DB5]" icon={Clock} />
        <Kpi testid="kpi-completed" label="Completed" value={data?.totals?.completed} tone="text-[#4f8d14]" icon={CheckCircle2} />
        <Kpi testid="kpi-overdue" label="Overdue" value={ov.total} tone="text-red-600" icon={AlertTriangle} onClick={() => drill({ status: 'Overdue' })} />
        <Kpi testid="kpi-avg-received" label="Avg Recv→Done" value={ct.avg_received_to_completed_days ?? '—'} sub={ct.avg_received_to_completed_days != null ? 'd' : ''} icon={TrendingUp} />
        <Kpi testid="kpi-avg-scheduled" label="Avg Sched→Done" value={ct.avg_scheduled_to_completed_days ?? '—'} sub={ct.avg_scheduled_to_completed_days != null ? 'd' : ''} icon={TrendingUp} />
      </div>

      {/* Overdue split */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        <button onClick={() => drill({ special: 'p1_emergency' })} data-testid="reports-p1-emergency"
          className="rounded-xl border border-red-200 bg-red-50 p-4 text-left hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-extrabold text-red-700">{ov.p1_emergency ?? 0}</div>
              <div className="text-xs font-semibold uppercase tracking-wide text-red-600 mt-0.5">P1 Emergency — Unscheduled / Overdue</div>
            </div>
            <AlertTriangle className="text-red-500" />
          </div>
        </button>
        <button onClick={() => drill({ special: 'overdue_other' })} data-testid="reports-other-overdue"
          className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-left hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-extrabold text-amber-700">{ov.other_overdue ?? 0}</div>
              <div className="text-xs font-semibold uppercase tracking-wide text-amber-600 mt-0.5">Other Overdue Work Orders (P2–P5)</div>
            </div>
            <Clock className="text-amber-500" />
          </div>
        </button>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <ChartCard title="Throughput — Created vs Completed" testid="chart-throughput">
          <ResponsiveContainer>
            <LineChart data={data?.throughput || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="week" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="created" stroke={BLUE} strokeWidth={2} name="Created" />
              <Line type="monotone" dataKey="completed" stroke={GREEN} strokeWidth={2} name="Completed" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Work Orders by Status" testid="chart-status">
          <ResponsiveContainer>
            <BarChart data={statusData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="status" tick={{ fontSize: 9 }} interval={0} angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill={NAVY} radius={[4, 4, 0, 0]} cursor="pointer"
                onClick={(d) => d && drill({ status: d.status })} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Work Orders by Priority" testid="chart-priority">
          <ResponsiveContainer>
            <BarChart data={priorityData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="priority" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} cursor="pointer"
                onClick={(d) => d && drill({ priority: d.priority })}>
                {priorityData.map((e, i) => <Cell key={i} fill={PRIORITY_COLORS[e.priority] || NAVY} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Work Orders by Trade" testid="chart-trade">
          <ResponsiveContainer>
            <BarChart data={tradeData} layout="vertical" margin={{ top: 5, right: 10, left: 40, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="trade_type" tick={{ fontSize: 10 }} width={90} />
              <Tooltip />
              <Bar dataKey="count" fill={GREEN} radius={[0, 4, 4, 0]} cursor="pointer"
                onClick={(d) => d && drill({ trade_type: d.trade_type })} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="table-by-customer">
          <CardContent className="p-0">
            <div className="p-4 pb-2"><h3 className="text-sm font-bold text-[#0E2A47]">By Customer</h3></div>
            <Table>
              <TableHeader><TableRow className="bg-gray-50"><TableHead>Customer</TableHead><TableHead className="text-right">Work Orders</TableHead><TableHead className="w-8" /></TableRow></TableHeader>
              <TableBody>
                {(data?.by_customer || []).slice(0, 12).map((c) => (
                  <TableRow key={c.customer_id || c.customer_name} className="cursor-pointer hover:bg-gray-50"
                    onClick={() => drill({ customer_id: c.customer_id, customer_name: c.customer_name })}
                    data-testid={`by-customer-${c.customer_id || 'none'}`}>
                    <TableCell className="font-medium text-slate-700">{c.customer_name}</TableCell>
                    <TableCell className="text-right font-semibold text-[#0E2A47]">{c.count}</TableCell>
                    <TableCell><ChevronRight size={14} className="text-slate-300" /></TableCell>
                  </TableRow>
                ))}
                {(data?.by_customer || []).length === 0 && <TableRow><TableCell colSpan={3} className="py-8 text-center text-slate-400 text-sm">No data in range.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card data-testid="table-provider-utilization">
          <CardContent className="p-0">
            <div className="p-4 pb-2"><h3 className="text-sm font-bold text-[#0E2A47]">Provider Utilization</h3></div>
            <Table>
              <TableHeader><TableRow className="bg-gray-50"><TableHead>Provider</TableHead><TableHead className="text-right">Active</TableHead><TableHead className="text-right">Done</TableHead><TableHead className="text-right">Avg Days</TableHead></TableRow></TableHeader>
              <TableBody>
                {(data?.provider_utilization || []).map((p) => (
                  <TableRow key={p.provider_id} data-testid={`prov-util-${p.provider_id}`}>
                    <TableCell className="font-medium text-slate-700">{p.company_name}</TableCell>
                    <TableCell className="text-right text-[#2F6DB5] font-semibold">{p.active}</TableCell>
                    <TableCell className="text-right text-[#4f8d14] font-semibold">{p.completed}</TableCell>
                    <TableCell className="text-right text-slate-500">{p.avg_completion_days ?? '—'}</TableCell>
                  </TableRow>
                ))}
                {(data?.provider_utilization || []).length === 0 && <TableRow><TableCell colSpan={4} className="py-8 text-center text-slate-400 text-sm">No provider-assigned work orders.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Service Requests — dedicated report + CSV export */}
      <Card className="mt-4" data-testid="sr-report-section">
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-3 p-4 pb-3 border-b border-gray-100">
            <div>
              <h3 className="flex items-center gap-2 text-sm font-bold text-[#0E2A47]"><FileSpreadsheet size={16} /> Service Requests</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {srBusy ? 'Loading…' : `${srReport?.rows?.length ?? 0} request${(srReport?.rows?.length ?? 0) === 1 ? '' : 's'} in the current filters`}. Honors your period, customer and priority filters and your reporting permissions.
              </p>
            </div>
            <Button size="sm" className="bg-[#0E2A47] hover:bg-[#16324F]" onClick={exportSrCsv}
              disabled={exporting || !(srReport?.rows?.length)} data-testid="sr-export-csv-btn">
              <Download size={15} className="mr-1.5" /> {exporting ? 'Preparing…' : 'Export CSV'}
            </Button>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  {(srReport?.columns || []).map((c) => (
                    <TableHead key={c} className="whitespace-nowrap text-[11px] uppercase tracking-wide">{c}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(srReport?.rows || []).slice(0, 100).map((row, i) => (
                  <TableRow key={row.request_number || i} data-testid={`sr-report-row-${i}`}>
                    {(srReport.keys || []).map((k) => (
                      <TableCell key={k} className="whitespace-nowrap text-slate-700">{row[k] || <span className="text-slate-300">—</span>}</TableCell>
                    ))}
                  </TableRow>
                ))}
                {!srBusy && (srReport?.rows || []).length === 0 && (
                  <TableRow><TableCell colSpan={(srReport?.columns || []).length || 14} className="py-8 text-center text-slate-400 text-sm">No service requests in the current filters.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {(srReport?.rows || []).length > 100 && (
            <div className="px-4 py-2 text-[11px] text-slate-400 border-t border-gray-100">Showing first 100 of {srReport.rows.length}. Export CSV for the full set.</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsView;
